/**
 * Schema Resolver - Grupo D12 Hub
 * 
 * Resolve problemas de schema entre public e grupod12
 * Garante que as consultas sejam direcionadas ao schema correto
 */

import { supabase, supabaseGrupoD12 } from '@/lib/supabase'
import type { Database } from '@/types/database'

type SupabaseClient = typeof supabase

interface SchemaConfig {
  schema: 'public' | 'grupod12'
  client: SupabaseClient
}

/**
 * Mapeamento de tabelas/views/functions para o schema correto
 */
const SCHEMA_MAPPING = {
  // Tabelas no schema grupod12
  tables: {
    'vendas': { schema: 'grupod12', client: supabaseGrupoD12 },
    'equipe': { schema: 'grupod12', client: supabaseGrupoD12 },
    'customers': { schema: 'grupod12', client: supabaseGrupoD12 },
    'meta_ads_performance': { schema: 'grupod12', client: supabaseGrupoD12 },
    'orbt_mv_creatives_complete_v2': { schema: 'grupod12', client: supabaseGrupoD12 }, // MV nova (15 dias) para /creativesv2
    'plataformas': { schema: 'grupod12', client: supabaseGrupoD12 },
    'plataformas_venda': { schema: 'grupod12', client: supabaseGrupoD12 },
    'mv_metaads_daily_total': { schema: 'grupod12', client: supabaseGrupoD12 }, // Materialized view (tratada como tabela)
    'mv_creatives_complete': { schema: 'grupod12', client: supabaseGrupoD12 }, // Materialized view otimizada para criativos
    'orbt_mv_creatives_hourly': { schema: 'grupod12', client: supabaseGrupoD12 }, // Materialized view para report diário por hora (novo desenvolvimento)
    'orbt_mv_campaigns_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Materialized view para report de campanhas (legado Meta)
    'orbt_v_campaigns_daily_redtrack': { schema: 'grupod12', client: supabaseGrupoD12 }, // View RedTrack – report Período /campanhas (single source)
    'orbt_mv_hourly_complete': { schema: 'grupod12', client: supabaseGrupoD12 }, // Materialized view otimizada para vendas por hora (novo desenvolvimento)
    'orbt_vturb_metrics': { schema: 'grupod12', client: supabaseGrupoD12 }, // Métricas do vTurb por ad/dia
    'orbt_redtrack_campaigns': { schema: 'grupod12', client: supabaseGrupoD12 }, // Campanhas RedTrack (payload completo)
    'orbt_redtrack_tracks': { schema: 'grupod12', client: supabaseGrupoD12 }, // Cliques RedTrack (últimos 15 dias)
    'orbt_redtrack_conversions': { schema: 'grupod12', client: supabaseGrupoD12 }, // Conversões RedTrack (últimos 15 dias)
    'orbt_redtrack_report_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Report diário RedTrack (GET /report) – receita/vendas
    'orbt_v_redtrack_creatives': { schema: 'grupod12', client: supabaseGrupoD12 }, // Creatives/ads por campanha RedTrack (do payload)
    'orbt_redtrack_report_full': { schema: 'grupod12', client: supabaseGrupoD12 }, // Report completo (colunas CSV) por campanha+dia
    'orbt_redtrack_ads_report': { schema: 'grupod12', client: supabaseGrupoD12 }, // Report por ad (rt_ad) RedTrack – /creativesv2 tab Período/Dia
    'orbt_redtrack_meta_campaigns_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Campanhas Meta por dia – tab Dia /campanhas e /creativesv2
    'orbt_redtrack_creatives': { schema: 'grupod12', client: supabaseGrupoD12 }, // Creatives com payload completo (sync campaign_day)
    'orbt_v_campaigns_report_redtrack': { schema: 'grupod12', client: supabaseGrupoD12 }, // View report com todas as colunas do CSV
    'orbt_redtrack_campaigns_import': { schema: 'grupod12', client: supabaseGrupoD12 }, // Import CSV RedTrack (Cost, Purchase, AOV Pago, Profit)
    'orbt_ad_player_mapping': { schema: 'grupod12', client: supabaseGrupoD12 }, // Mapeamento ad_id → player_id
    'call_center_redirects': { schema: 'grupod12', client: supabaseGrupoD12 }, // Config redirecionamento de leads
    'fulfillment_rates_log': { schema: 'grupod12', client: supabaseGrupoD12 }, // Log de taxas de fulfillment por loja
    'postback_error_log': { schema: 'grupod12', client: supabaseGrupoD12 }, // Log de erros de postback
    'user_page_views': { schema: 'grupod12', client: supabaseGrupoD12 }, // Última visualização de páginas por usuário
    'call_center_failed_leads': { schema: 'grupod12', client: supabaseGrupoD12 }, // Leads com erro no workflow
    'mv_orbt_meta_campaign_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // MV campanhas Meta por dia (Jan 2026+)
    'mv_orbt_meta_campaign_daily_v2': { schema: 'grupod12', client: supabaseGrupoD12 }, // MV campanhas Meta por dia v2 (inline_link_clicks)
    'orbt_cartpanda_orders': { schema: 'grupod12', client: supabaseGrupoD12 }, // Pedidos CartPanda com campaign_id Meta
    'orbt_clickbank_orders': { schema: 'grupod12', client: supabaseGrupoD12 }, // Pedidos ClickBank com meta_campaign_id
    'orbt_vturb_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Métricas diárias VTurb por player
    'orbt_vturb_campaign_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Métricas VTurb por campaign_name por dia (traffic_origin_stats)
    'orbt_meta_campaign_hourly_spend': { schema: 'grupod12', client: supabaseGrupoD12 }, // Snapshots horários de gasto acumulado por campanha (novo, independente)
    'orbt_meta_creative_hourly_spend': { schema: 'grupod12', client: supabaseGrupoD12 }, // Snapshots horários de gasto acumulado por criativo/ad (novo, independente)
    'mv_orbt_campanhas_periodo': { schema: 'grupod12', client: supabaseGrupoD12 }, // MV campanhas período (tab Período /campanhas)
    'mv_orbt_criativos_periodo': { schema: 'grupod12', client: supabaseGrupoD12 }, // MV criativos período (tab Período /creativesv2)
    'mv_orbt_meta_ad_daily': { schema: 'grupod12', client: supabaseGrupoD12 }, // Tabela ad-level diária (base para mv_orbt_criativos_periodo)
    'orbt_clickup_criativos': { schema: 'grupod12', client: supabaseGrupoD12 }, // Mapeamento criativo → copywriter/editor (sync ClickUp via n8n)
    'meta_ads_config': { schema: 'grupod12', client: supabaseGrupoD12 }, // Configurações do Meta Ads (tokens, API params)
    'orbt_meta_ads_month_close': { schema: 'grupod12', client: supabaseGrupoD12 }, // Meses fechados com gasto Meta Ads do cartão (/auditoria)
    'contas_anuncio_meta': { schema: 'grupod12', client: supabaseGrupoD12 }, // Contas de anúncio do Meta
  } as const,

  // Views no schema grupod12
  views: {
    'v_metaads_daily_total': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_metaads_daily_total_ajustada': { schema: 'grupod12', client: supabaseGrupoD12 }, // View com ajustes RedTrack
    'v_metaads_daily_campaign_adset_ad_performance': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_metaads_daily_campaign_performance': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_analytics_performance': { schema: 'grupod12', client: supabaseGrupoD12 },
    // Novas views para creatives
    'v_metaads_creatives_performance': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_metaads_creatives_aggregated': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_creatives_sales_tracking': { schema: 'grupod12', client: supabaseGrupoD12 },
    'v_creatives_complete': { schema: 'grupod12', client: supabaseGrupoD12 }, // Complete view with account_name, all video metrics, sales, ROAS and profit
    'orbt_v_creatives_hourly_report': { schema: 'grupod12', client: supabaseGrupoD12 }, // Hourly breakdown of spend and sales for daily report (novo desenvolvimento)
    'orbt_v_creatives_funnel': { schema: 'grupod12', client: supabaseGrupoD12 }, // View combinada Meta Ads + vTurb com métricas de funil (legado)
    'orbt_v_creatives_funnel_v2': { schema: 'grupod12', client: supabaseGrupoD12 }, // View V2 (10 dias) para /creativesv2 (Tabela)
    'orbt_v_active_players': { schema: 'grupod12', client: supabaseGrupoD12 }, // Players vTurb ativos para sync
    'v_call_center_redirects_active': { schema: 'grupod12', client: supabaseGrupoD12 }, // View de configs de call center ativas
    'orbt_v_campanhas_periodo': { schema: 'grupod12', client: supabaseGrupoD12 }, // View unificada Meta+CartPanda+VTurb para tab Período /campanhas
    'orbt_v_criativos_periodo': { schema: 'grupod12', client: supabaseGrupoD12 }, // View unificada Meta+CartPanda+RedTrack para tab Período /creativesv2
  } as const,

  // Functions RPC no schema grupod12
  functions: {
    'force_refresh_marketing_data': { schema: 'grupod12', client: supabaseGrupoD12 },
    'process_recent_ad_spend_safe': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_dashboard_metrics': { schema: 'grupod12', client: supabaseGrupoD12 },
    'refresh_marketing_views': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_ads_hierarchy_with_spend': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_ads_hierarchy_hourly_with_commission': { schema: 'grupod12', client: supabaseGrupoD12 },
    'refresh_marketing_costs_daily': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_creatives_metrics': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_fulfillment_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_daily_fulfillment_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    // Funções combinadas (invoices + splits)
    'get_fulfillment_combined_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_daily_fulfillment_combined_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_fulfillment_invoices_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_daily_fulfillment_invoices_by_store': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_distinct_products': { schema: 'grupod12', client: supabaseGrupoD12 },
    'fn_orbt_criativos_periodo': { schema: 'grupod12', client: supabaseGrupoD12 },
    // Funções otimizadas para Cash Flow
    'get_bank_balances_aggregated': { schema: 'grupod12', client: supabaseGrupoD12 },
    'get_platform_balances_aggregated': { schema: 'grupod12', client: supabaseGrupoD12 },
    // Função otimizada para report diário por hora
    'orbt_get_creatives_hourly_by_day': { schema: 'grupod12', client: supabaseGrupoD12 },
  } as const,
} as const

type TableName = keyof typeof SCHEMA_MAPPING.tables
type ViewName = keyof typeof SCHEMA_MAPPING.views
type FunctionName = keyof typeof SCHEMA_MAPPING.functions

/**
 * Obtém o cliente correto para uma tabela
 */
export function getTableClient(tableName: string): SupabaseClient {
  const config = SCHEMA_MAPPING.tables[tableName as TableName]
  if (config) {
    return config.client
  }

  // Fallback silencioso para public schema - não logar para evitar ruído
  return supabase
}

/**
 * Obtém o cliente correto para uma view
 */
export function getViewClient(viewName: string): SupabaseClient {
  const config = SCHEMA_MAPPING.views[viewName as ViewName]
  if (config) {
    return config.client
  }

  // Fallback silencioso para public schema - não logar para evitar ruído
  return supabase
}

/**
 * Obtém o cliente correto para uma função RPC
 */
export function getRpcClient(functionName: string): SupabaseClient {
  const config = SCHEMA_MAPPING.functions[functionName as FunctionName]
  if (config) {
    return config.client
  }

  // Fallback silencioso para public schema - não logar para evitar ruído
  return supabase
}

/**
 * Query builder que automaticamente usa o schema correto
 */
export const schemaQuery = {
  /**
   * Consulta uma tabela no schema correto
   */
  table<T = any>(tableName: string) {
    const client = getTableClient(tableName)
    return client.from(tableName as any) as ReturnType<typeof client.from>
  },

  /**
   * Consulta uma view no schema correto
   */
  view<T = any>(viewName: string) {
    const client = getViewClient(viewName)
    return client.from(viewName as any) as ReturnType<typeof client.from>
  },

  /**
   * Chama uma função RPC no schema correto
   */
  rpc<T = any>(functionName: string, params?: any) {
    const client = getRpcClient(functionName)
    return client.rpc(functionName as any, params) as ReturnType<typeof client.rpc>
  }
}

/**
 * Helper para debug - lista todos os mapeamentos de schema
 * Só funciona em ambiente de desenvolvimento
 */
export function debugSchemaMappings() {
  if (import.meta.env.DEV) {
    console.group('📋 Schema Mappings Debug')
    console.log('📊 Tables (grupod12):', Object.keys(SCHEMA_MAPPING.tables))
    console.log('👁️ Views (grupod12):', Object.keys(SCHEMA_MAPPING.views))
    console.log('⚙️ Functions (grupod12):', Object.keys(SCHEMA_MAPPING.functions))
    console.groupEnd()
  }
}

/**
 * Valida se uma consulta deve usar schema específico
 */
export function validateSchemaUsage(
  type: 'table' | 'view' | 'function',
  name: string
): { isValid: boolean; expectedSchema: string; suggestion?: string } {
  const mappings = {
    table: SCHEMA_MAPPING.tables,
    view: SCHEMA_MAPPING.views,
    function: SCHEMA_MAPPING.functions,
  }

  const config = mappings[type][name as any]

  if (config) {
    return {
      isValid: true,
      expectedSchema: config.schema,
      suggestion: `Use schemaQuery.${type}('${name}') instead of direct supabase.from('${name}')`
    }
  }

  return {
    isValid: false,
    expectedSchema: 'unknown',
    suggestion: `Add ${name} to schema mappings if it exists in grupod12 schema`
  }
}

// Export dos clientes originais para casos especiais
export { supabase as publicClient, supabaseGrupoD12 as grupod12Client }